import { useState, useEffect } from 'react';
import { CheckCircle2, AlertCircle, Clock, RefreshCw } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const API_URL = import.meta.env.VITE_API_URL ?? '';

const COMPONENTS = [
  { id: 'api',        label: 'API',                    description: 'Core REST endpoints and authentication' },
  { id: 'ai',         label: 'AI Generation Engine',   description: 'Claude-powered website generation' },
  { id: 'storage',    label: 'Website Storage',        description: 'Supabase database and file storage' },
  { id: 'payments',   label: 'Payment Processing',     description: 'Flutterwave and NOWPayments gateways' },
  { id: 'email',      label: 'Email Delivery',         description: 'Transactional email via Hostinger SMTP' },
];

function StatusBadge({ status }) {
  if (status === 'loading') {
    return (
      <span className="flex items-center gap-1.5 text-zinc-500 text-xs font-medium">
        <Clock className="w-3.5 h-3.5 animate-pulse" /> Building...
      </span>
    );
  }
  if (status === 'operational') {
    return (
      <span className="flex items-center gap-1.5 text-emerald-400 text-xs font-semibold">
        Operational
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-red-400 text-xs font-semibold">
      Degraded
    </span>
  );
}

export default function Status() {
  const [statuses, setStatuses] = useState(
    Object.fromEntries(COMPONENTS.map(c => [c.id, 'loading']))
  );
  const [lastChecked, setLastChecked] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  async function checkStatus() {
    setRefreshing(true);
    const next = { ...Object.fromEntries(COMPONENTS.map(c => [c.id, 'loading'])) };

    try {
      const res = await fetch(`${API_URL}/api/health`, { signal: AbortSignal.timeout(5000) });
      next.api     = res.ok ? 'operational' : 'degraded';
      next.storage  = res.ok ? 'operational' : 'degraded';
    } catch {
      next.api     = 'degraded';
      next.storage = 'degraded';
    }

    // AI, Payments, Email — we can't ping these cheaply from the browser,
    // so we trust their status unless the API itself is degraded.
    const apiUp = next.api === 'operational';
    next.ai       = apiUp ? 'operational' : 'degraded';
    next.payments = apiUp ? 'operational' : 'degraded';
    next.email    = apiUp ? 'operational' : 'degraded';

    setStatuses(next);
    setLastChecked(new Date());
    setRefreshing(false);
  }

  useEffect(() => { checkStatus(); }, []);

  const allOperational = Object.values(statuses).every(s => s === 'operational');

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-20">
      <SEOHead
        title="System Status — BuildersLab"
        description="Live operational status for BuildersLab's AI website builder, payments, and infrastructure."
        ogUrl="https://builderslab.net/Status"
        noIndex={false}
      />
      <div className="max-w-3xl mx-auto px-6">

        <div
         
         
         
          className="mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold uppercase tracking-wider mb-6">
            System Status
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 tracking-tight">
            BuildersLab Status
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed">
            Live health of our infrastructure and services.
          </p>
        </div>

        {/* Overall status banner */}
        <div
         
         
         
          className={`rounded-2xl border p-5 mb-8 flex items-center justify-between ${
            allOperational
              ? 'bg-emerald-500/5 border-emerald-500/20'
              : 'bg-red-500/5 border-red-500/20'
          }`}
        >
          <div className="flex items-center gap-3">
            {allOperational
              ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              : <AlertCircle className="w-5 h-5 text-red-400" />
            }
            <span className="font-semibold text-white">
              {allOperational ? 'All systems operational' : 'Some systems are experiencing issues'}
            </span>
          </div>
          <button
            onClick={checkStatus}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Component statuses */}
        <div
         
         
         
          className="rounded-2xl border border-zinc-800 overflow-hidden mb-8"
        >
          {COMPONENTS.map((component, i) => (
            <div
              key={component.id}
              className={`flex items-center justify-between px-6 py-4 ${
                i < COMPONENTS.length - 1 ? 'border-b border-zinc-800' : ''
              }`}
            >
              <div>
                <p className="text-white text-sm font-semibold">{component.label}</p>
                <p className="text-zinc-500 text-xs mt-0.5">{component.description}</p>
              </div>
              <StatusBadge status={statuses[component.id]} />
            </div>
          ))}
        </div>

        {lastChecked && (
          <p className="text-zinc-600 text-xs text-center">
            Last checked {lastChecked.toLocaleTimeString()}
          </p>
        )}

        <div
         
         
         
          className="mt-12 text-center"
        >
          <p className="text-zinc-500 text-sm">
            Experiencing an issue not reflected above?{' '}
            <a href="mailto:support@builderslab.net" className="text-orange-400 hover:underline">
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
